(() => {
  "use strict";

  const root = document.querySelector("[data-dealer-signing]");
  if (!root) return;

  const relayUrl = String(root.dataset.relayUrl || "").replace(/\/$/, "");
  const supportEmail = String(root.dataset.supportEmail || "sales@trailergearbox.com");
  const state = { browserSession: "", consentVersion: "", documents: [], documentIndex: 0, viewedDocuments: new Set(), signingReady: false, previewOnly: false, submitting: false, completed: false, strokes: [], currentStroke: null, drawing: false };
  let invitationToken = "";
  const q = (selector) => root.querySelector(selector);
  const qa = (selector) => [...root.querySelectorAll(selector)];
  const canvas = q("[data-signature-canvas]");
  const context = canvas.getContext("2d", { alpha: true });

  qa("[data-support-link]").forEach((link) => { link.href = `mailto:${supportEmail}`; link.textContent = supportEmail; });

  function showState(name) {
    qa("[data-state]").forEach((element) => { element.hidden = element.dataset.state !== name; });
  }

  function fail(message) {
    q("[data-error-message]").textContent = message || "This link might be expired, revoked, or already completed.";
    showState("error");
  }

  async function request(path, options = {}) {
    if (!relayUrl) throw new Error("The signing page has not been connected yet. Please contact Trailer Gearbox Sales.");
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(`${relayUrl}/${path}`, {
        method: "POST",
        cache: "no-store",
        referrerPolicy: "no-referrer",
        headers: { "Content-Type": "application/json", ...(options.headers || {}) },
        body: JSON.stringify(options.body || {}),
        signal: controller.signal,
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "The signing service is temporarily unavailable.");
      return payload;
    } catch (error) {
      if (controller.signal.aborted) throw new Error(path === "submit"
        ? "Submission confirmation timed out. Your signature is still here. Retry to recover the receipt for this agreement."
        : "Opening the agreement timed out. Please try again.");
      throw error;
    } finally { clearTimeout(timer); }
  }

  function fragmentToken() {
    const fragment = window.location.hash.replace(/^#/, "");
    const params = new URLSearchParams(fragment);
    return params.get("token") || (fragment && !fragment.includes("=") ? decodeURIComponent(fragment) : "");
  }

  function cleanAddress() {
    history.replaceState(null, document.title, `${location.pathname}${location.search}`);
  }

  function documentKey(documentItem, index) {
    return String(documentItem?.label || `document-${index}`).trim().toLowerCase();
  }

  function isDealerAgreement(documentItem) {
    return String(documentItem?.label || "").trim().toLowerCase() === "dealer agreement";
  }

  function supportingDocumentsComplete() {
    return state.documents
      .map((documentItem, index) => ({ documentItem, index }))
      .filter(({ documentItem }) => !isDealerAgreement(documentItem))
      .every(({ documentItem, index }) => state.viewedDocuments.has(documentKey(documentItem, index)));
  }

  function updateSigningAvailability(selected) {
    state.signingReady = supportingDocumentsComplete() && isDealerAgreement(selected);
    const form = q("[data-signing-form]");
    form.classList.toggle("is-locked", !state.signingReady);
    form.classList.toggle("is-ready", state.signingReady);
    form.setAttribute("aria-disabled", state.signingReady ? "false" : "true");
    q("[data-signing-status]").textContent = state.signingReady ? "Ready to complete" : "Locked";
    q("[data-signing-help]").textContent = state.signingReady
      ? "Confirm your legal name, acknowledge the dealer program, and add your signature."
      : "Review the four dealer documents to unlock this section.";
    qa("[data-signing-form] input, [data-signing-form] button").forEach((control) => {
      control.disabled = !state.signingReady || state.submitting || state.completed || (state.previewOnly && control.matches("[data-submit]"));
    });
    canvas.tabIndex = state.signingReady ? 0 : -1;
    canvas.setAttribute("aria-disabled", state.signingReady ? "false" : "true");
    if (state.previewOnly) q("[data-submit]").textContent = "Preview only — submission disabled";
  }

  function selectDocument(index) {
    const selected = state.documents[index];
    if (!selected) return;
    if (isDealerAgreement(selected) && !supportingDocumentsComplete()) return;
    state.documentIndex = index;
    state.viewedDocuments.add(documentKey(selected, index));
    renderDocuments();
  }

  function renderDocuments() {
    const list = q("[data-document-list]");
    list.innerHTML = "";
    const supportingComplete = supportingDocumentsComplete();
    state.documents.forEach((documentItem, index) => {
      const button = document.createElement("button");
      button.type = "button";
      const isAgreement = isDealerAgreement(documentItem);
      const isViewed = state.viewedDocuments.has(documentKey(documentItem, index));
      const isLocked = isAgreement && !supportingComplete;
      button.innerHTML = `<span class="dealer-signing__document-number">${index + 1}</span><span class="dealer-signing__document-label"></span><span class="dealer-signing__document-status">${isViewed && !isAgreement ? "Viewed" : isLocked ? "Review steps 1–4 first" : isAgreement ? "Fill and sign" : "Review document"}</span>`;
      button.querySelector(".dealer-signing__document-label").textContent = documentItem.label || `Document ${index + 1}`;
      button.setAttribute("aria-current", index === state.documentIndex ? "true" : "false");
      button.dataset.viewed = isViewed && !isAgreement ? "true" : "false";
      button.dataset.locked = isLocked ? "true" : "false";
      button.disabled = isLocked;
      if (isLocked) button.title = "Review the four dealer documents before opening the Dealer Agreement.";
      button.addEventListener("click", () => selectDocument(index));
      list.appendChild(button);
    });
    const selected = state.documents[state.documentIndex] || {};
    const url = String(selected.url || "");
    q("[data-document-title]").textContent = selected.label || "Dealer Agreement";
    q("[data-document-position]").textContent = `Document ${state.documentIndex + 1} of ${state.documents.length}`;
    q("[data-document-frame]").src = url ? `${url}#toolbar=1&navpanes=0&view=FitH` : "about:blank";
    q("[data-document-open]").href = url || "#";
    q("[data-document-fallback-link]").href = url || "#";
    q("[data-document-previous]").disabled = state.documentIndex === 0;
    q("[data-document-next]").disabled = state.documentIndex >= state.documents.length - 1;
    q("[data-document-next]").textContent = state.documentIndex === state.documents.length - 2 ? "Continue to Fill and Sign" : "Next";
    updateSigningAvailability(selected);
    qa("[data-document-list] button")[state.documentIndex]?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }

  function resizeCanvas() {
    const rect = canvas.getBoundingClientRect();
    const ratio = Math.max(1, Math.min(window.devicePixelRatio || 1, 3));
    const width = Math.max(1, Math.round(rect.width * ratio));
    const height = Math.max(1, Math.round(rect.height * ratio));
    if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; }
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    drawSignature(rect.width, rect.height);
  }

  function drawSignature(width = canvas.clientWidth, height = canvas.clientHeight) {
    context.clearRect(0, 0, width, height);
    context.strokeStyle = "#111111";
    context.lineCap = "round";
    context.lineJoin = "round";
    context.lineWidth = 2.2;
    state.strokes.forEach((stroke) => {
      if (stroke.length < 2) return;
      context.beginPath();
      stroke.forEach((point, index) => {
        const x = point.x * width;
        const y = point.y * height;
        if (index === 0) context.moveTo(x, y); else context.lineTo(x, y);
      });
      context.stroke();
    });
  }

  function normalizedPoint(event) {
    const rect = canvas.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height)),
      t: Math.round(performance.now()),
      p: Number.isFinite(event.pressure) && event.pressure > 0 ? event.pressure : 0.5,
    };
  }

  function pointerDown(event) {
    if (!state.signingReady || state.submitting || state.completed) return;
    event.preventDefault();
    canvas.setPointerCapture(event.pointerId);
    state.currentStroke = [normalizedPoint(event)];
    state.strokes.push(state.currentStroke);
    state.drawing = true;
  }

  function pointerMove(event) {
    if (!state.drawing || !state.currentStroke) return;
    event.preventDefault();
    const point = normalizedPoint(event);
    const previous = state.currentStroke[state.currentStroke.length - 1];
    if (Math.hypot(point.x - previous.x, point.y - previous.y) < 0.0015) return;
    state.currentStroke.push(point);
    drawSignature();
  }

  function pointerUp(event) {
    if (!state.drawing) return;
    event.preventDefault();
    state.drawing = false;
    state.currentStroke = null;
    if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
    drawSignature();
  }

  function signatureSize() {
    const points = state.strokes.flat();
    if (!points.length) return { points: 0, width: 0, height: 0 };
    const xs = points.map((point) => point.x);
    const ys = points.map((point) => point.y);
    return { points: points.length, width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) };
  }

  async function submit(event) {
    event.preventDefault();
    if (state.submitting || state.completed || state.previewOnly) return;
    const signerName = q("[data-signer-name]").value.trim();
    const signerTitle = q("[data-signer-title]").value.trim();
    const nameConfirmed = q("[data-name-confirmation]").checked;
    const accepted = q("[data-acknowledgment]").checked;
    const size = signatureSize();
    let error = "";
    if (!state.signingReady) error = "Review all four dealer documents and open the Dealer Agreement before signing.";
    else if (signerName.length < 2) error = "Enter your full legal name.";
    else if (!nameConfirmed) error = "Confirm that this is your full legal name.";
    else if (signerTitle.length < 2) error = "Enter your business title.";
    else if (!accepted) error = "Review and accept the dealer-program acknowledgment.";
    else if (size.points < 12 || size.width < 0.08 || size.height < 0.03) error = "Add a complete signature in the signature box.";
    const errorElement = q("[data-form-error]");
    errorElement.textContent = error;
    errorElement.hidden = !error;
    if (error) return;

    state.submitting = true;
    updateSigningAvailability(state.documents[state.documentIndex]);
    const button = q("[data-submit]");
    button.disabled = true;
    button.textContent = "Submitting…";
    try {
      const payload = await request("submit", {
        headers: { "X-Dealer-Browser-Session": state.browserSession },
        body: {
          signer_name: signerName,
          signer_title: signerTitle,
          acknowledgment_accepted: true,
          consent_version: state.consentVersion,
          signature_strokes: state.strokes,
          client_timestamp: new Date().toISOString(),
        },
      });
      if (!payload.receipt_id || !payload.submitted_at) throw new Error("Submission confirmation was incomplete. Retry to recover the receipt for this agreement.");
      state.completed = true;
      q("[data-receipt-id]").textContent = payload.receipt_id || "Recorded";
      q("[data-submitted-at]").textContent = new Date(payload.submitted_at || Date.now()).toLocaleString();
      showState("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (submissionError) {
      errorElement.textContent = submissionError.message || "The signature could not be submitted. Please try again.";
      errorElement.hidden = false;
      button.disabled = state.previewOnly;
      button.textContent = state.previewOnly ? "Preview only — submission disabled" : "Sign and submit";
    } finally {
      state.submitting = false;
      updateSigningAvailability(state.documents[state.documentIndex]);
    }
  }

  async function initialize() {
    try {
      const token = invitationToken || fragmentToken();
      if (!token) throw new Error("Open the secure signing link from your Trailer Gearbox email.");
      invitationToken = token;
      cleanAddress();
      const payload = await request("exchange", { body: { token } });
      if (!payload.browser_session || !payload.consent_version) throw new Error("The signing service returned an incomplete agreement. Please try again.");
      state.browserSession = payload.browser_session;
      state.consentVersion = payload.consent_version;
      state.previewOnly = payload.preview_only === true;
      const availableDocuments = Array.isArray(payload.documents) ? payload.documents.filter((item) => item && item.url) : [];
      state.documents = [
        ...availableDocuments.filter((item) => !isDealerAgreement(item)),
        ...availableDocuments.filter((item) => isDealerAgreement(item)),
      ];
      const expectedLabels = ["Dealer Agreement", "MAP Policy", "Dealer Price List", "Product Warranty Policy", "Dealer Program Overview"];
      if (state.documents.length !== 5 || expectedLabels.some(label => state.documents.filter(item => item.label === label).length !== 1)
          || state.documents.some(item => !String(item.url).startsWith("https://"))) {
        throw new Error("The dealer packet is incomplete. Please contact Trailer Gearbox Sales.");
      }
      invitationToken = "";
      const dealer = payload.dealer || {};
      q("[data-business-name]").value = dealer.business_name || "";
      q("[data-signer-name]").value = dealer.contact_name || "";
      q("[data-signer-title]").value = dealer.signer_title || "";
      q("[data-expiration]").textContent = `This secure link expires ${new Date(payload.expires_at).toLocaleString()}.`;
      selectDocument(0);
      showState("ready");
      requestAnimationFrame(resizeCanvas);
    } catch (error) {
      fail(error.message);
      q("[data-retry-opening]").hidden = !invitationToken;
    }
  }

  q("[data-document-previous]").addEventListener("click", () => { if (state.documentIndex > 0) selectDocument(state.documentIndex - 1); });
  q("[data-document-next]").addEventListener("click", () => { if (state.documentIndex < state.documents.length - 1) selectDocument(state.documentIndex + 1); });
  q("[data-signature-clear]").addEventListener("click", () => { state.strokes = []; drawSignature(); canvas.focus(); });
  q("[data-signing-form]").addEventListener("submit", submit);
  q("[data-retry-opening]").addEventListener("click", () => { showState("loading"); initialize(); });
  canvas.addEventListener("pointerdown", pointerDown);
  canvas.addEventListener("pointermove", pointerMove);
  canvas.addEventListener("pointerup", pointerUp);
  canvas.addEventListener("pointercancel", pointerUp);
  new ResizeObserver(resizeCanvas).observe(q("[data-signature-wrap]"));
  initialize();
})();
